/**
 * UIManager - Manages all UI panels
 */

import { Vector2 } from '../math/Vector2';
import { UIPanel } from './UIPanel';
import { HUD } from './HUD';
import { ToastManager } from './ToastManager';

export class UIManager {
  private panels: Map<string, UIPanel> = new Map();
  private hud: HUD;
  private toastManager: ToastManager;
  private activePanel: UIPanel | null = null;

  constructor() {
    this.hud = new HUD();
    this.toastManager = new ToastManager();
  }

  /** Register a panel */
  registerPanel(name: string, panel: UIPanel): void {
    this.panels.set(name, panel);
  }

  /** Get a panel by name */
  getPanel<T extends UIPanel>(name: string): T | undefined {
    return this.panels.get(name) as T | undefined;
  }

  /** Get HUD */
  getHUD(): HUD {
    return this.hud;
  }

  /** Show a panel by name */
  showPanel(name: string): void {
    const panel = this.panels.get(name);
    if (panel) {
      panel.show();
      this.activePanel = panel;
    }
  }

  /** Hide a panel by name */
  hidePanel(name: string): void {
    const panel = this.panels.get(name);
    if (panel) {
      panel.hide();
      if (this.activePanel === panel) {
        this.activePanel = null;
      }
    }
  }

  /** Toggle a panel by name */
  togglePanel(name: string): void {
    const panel = this.panels.get(name);
    if (panel) {
      const wasVisible = panel.getIsVisible();

      // Close all other panels first
      this.hideAll();

      // If the panel wasn't visible before, show it
      if (!wasVisible) {
        panel.show();
        this.activePanel = panel;
      }
    }
  }

  /** Hide all panels */
  hideAll(): void {
    for (const panel of this.panels.values()) {
      panel.hide();
    }
    this.activePanel = null;
  }

  /** Check if any panel is visible */
  hasVisiblePanel(): boolean {
    for (const panel of this.panels.values()) {
      if (panel.getIsVisible()) return true;
    }
    return false;
  }

  /** Check if a specific panel is visible */
  isPanelVisible(name: string): boolean {
    const panel = this.panels.get(name);
    return panel?.getIsVisible() ?? false;
  }

  /** Check if a point is over any visible panel */
  isOverPanel(pos: Vector2): boolean {
    for (const panel of this.panels.values()) {
      if (panel.getIsVisible() && panel.containsPoint(pos)) {
        return true;
      }
    }
    return false;
  }

  /** Update all panels */
  update(deltaTime: number): void {
    for (const panel of this.panels.values()) {
      if (panel.getIsVisible()) {
        panel.update(deltaTime);
      }
    }
    this.toastManager.update(deltaTime);
  }

  /** Handle mouse down */
  handleMouseDown(pos: Vector2): boolean {
    // Check panels in reverse order (top panels first)
    const panels = Array.from(this.panels.values()).reverse();
    for (const panel of panels) {
      if (panel.getIsVisible() && panel.handleMouseDown(pos)) {
        return true;
      }
    }
    return false;
  }

  /** Handle mouse up */
  handleMouseUp(pos: Vector2): boolean {
    for (const panel of this.panels.values()) {
      if (panel.getIsVisible() && panel.handleMouseUp(pos)) {
        return true;
      }
    }
    return false;
  }

  /** Handle mouse move */
  handleMouseMove(pos: Vector2): boolean {
    for (const panel of this.panels.values()) {
      if (panel.getIsVisible() && panel.handleMouseMove(pos)) {
        return true;
      }
    }
    return false;
  }

  /** Handle click */
  handleClick(pos: Vector2): boolean {
    // Check panels in reverse order (top panels first)
    const panels = Array.from(this.panels.values()).reverse();
    for (const panel of panels) {
      if (panel.getIsVisible() && panel.handleClick(pos)) {
        return true;
      }
    }
    return false;
  }

  /** Render all UI */
  render(ctx: CanvasRenderingContext2D, screenWidth: number, screenHeight: number): void {
    // Render HUD first (always visible)
    this.hud.render(ctx, screenWidth, screenHeight);

    // Render panels
    for (const panel of this.panels.values()) {
      panel.render(ctx);
    }

    // Render toasts on top
    this.toastManager.render(ctx, screenWidth, screenHeight);
  }

  /** Show a toast notification */
  showToast(message: string, duration?: number, color?: string): void {
    this.toastManager.show(message, duration, color);
  }

  /** Show a success toast */
  showSuccessToast(message: string): void {
    this.toastManager.success(message);
  }

  /** Show an info toast */
  showInfoToast(message: string): void {
    this.toastManager.info(message);
  }

  /** Center a panel on screen */
  centerPanel(name: string, screenWidth: number, screenHeight: number): void {
    const panel = this.panels.get(name);
    if (panel) {
      panel.centerOnScreen(screenWidth, screenHeight);
    }
  }
}
